import { computed } from 'vue'
import type { RouteLocationRaw, RouteRecordName } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import { useAppMode } from '@/vue/composables/use-app-mode'
import type { AppMode } from '@/vue/composables/use-app-mode'
import { useAssetOnlyAccess } from '@/vue/composables/use-asset-only-access'
import { canAccessFeature } from '@/vue/composables/use-can-access-feature'
import type { BreadcrumbAsset, BreadcrumbCompany, BreadcrumbPortfolio } from './breadcrumb.types'
import { useCompanyHierarchy } from './use-company-hierarchy'

export function useNavigationRoutes() {
    const { mode: currentMode } = useAppMode()
    const route = useRoute()
    const router = useRouter()
    const isAssetOnlyAccess = useAssetOnlyAccess()
    const { portfolios } = useCompanyHierarchy()

    /**
     * TEMPORARY SOLUTION for reporting year resolution.
     *
     * Better approaches to consider:
     * - Remove reporting_year from route params, move to query param
     * - Handle year resolution in layout pages (AssetLayout, PortfolioLayout, CompanyLayout)
     *   that act as umbrella components for their respective entity routes
     *
     * TBD.
     */
    const fallbackYear = new Date().getFullYear() - 1
    const hasSinglePortfolio = computed(() => portfolios.value.length === 1)

    function getRouteYear(): number | undefined {
        if ('reporting_year' in route.params) {
            const routeYear = Number(route.params.reporting_year)
            return !Number.isNaN(routeYear) && routeYear > 0 ? routeYear : undefined
        }
        return undefined
    }

    function resolveCompanyReportingYear(company: BreadcrumbCompany): number {
        return getRouteYear()
          ?? company.last_reporting_year
          ?? fallbackYear
    }

    function resolvePortfolioReportingYear(portfolio: BreadcrumbPortfolio): number {
        return getRouteYear()
          ?? portfolio.default_reporting_year
          ?? fallbackYear
    }

    function resolveAssetReportingYear(asset: BreadcrumbAsset): number {
        return getRouteYear()
          ?? asset.portfolio.default_reporting_year
          ?? fallbackYear
    }

    function companyRoute(company: BreadcrumbCompany): RouteLocationRaw {
        if (currentMode.value === 'reports') {
            return {
                name: 'reports.company.overview',
                params: { reporting_year: resolveCompanyReportingYear(company) },
            }
        }

        if (currentMode.value === 'collection') {
            return {
                name: 'company.collection.overview',
                params: { reporting_year: resolveCompanyReportingYear(company) },
            }
        }

        if (currentMode.value === 'analytics') {
            const name: RouteRecordName = company.asset_only_access
                ? 'company.assets'
                : 'company.overview'

            return {
                name,
                params: {
                    reporting_year: resolveCompanyReportingYear(company),
                },
            }
        }

        throw new Error(`Invalid mode: ${currentMode.value} to determine company route`)
    }

    function companyRoutePreserving(company: BreadcrumbCompany): RouteLocationRaw {
        const isOnHomePage = route.name?.toString()?.startsWith('home') ?? false
        const routeName = route.name?.toString() ?? ''

        if (isOnHomePage) {
            // Stay on home page - company selection is handled by global state
            return { name: 'home' }
        }

        const isOnCompanyPage = routeName.startsWith('company.')
          || routeName.startsWith('reports.company.')

        if (isOnCompanyPage) {
            return {
                name: route.name,
                params: {
                    ...route.params,
                    reporting_year: resolveCompanyReportingYear(company),
                },
            } as RouteLocationRaw
        }

        return companyRoute(company)
    }

    function portfolioRoute(portfolio: BreadcrumbPortfolio, { mode }: { mode?: AppMode } = {}): RouteLocationRaw {
        const params = {
            portfolio_id: portfolio.id,
            reporting_year: resolvePortfolioReportingYear(portfolio),
        }

        const computedMode = mode ?? currentMode.value

        if (computedMode === 'reports') {
            return { name: 'reports.portfolio.overview', params }
        }

        if (computedMode === 'collection') {
            return { name: 'portfolio.input-overview', params }
        }

        if (computedMode === 'analytics') {
            return { name: 'portfolio.overview', params }
        }

        throw new Error(`Invalid mode: ${computedMode} to determine portfolio route`)
    }

    function portfolioListRoute(portfolio: BreadcrumbPortfolio): RouteLocationRaw {
        return {
            name: currentMode.value === 'collection' ? 'portfolio-asset.collection.list' : 'portfolio-asset.list',
            params: {
                portfolio_id: portfolio.id,
                reporting_year: resolvePortfolioReportingYear(portfolio),
            },
        }
    }

    function assetRoute(asset: BreadcrumbAsset): RouteLocationRaw {
        return {
            name: currentMode.value === 'collection' ? 'asset.input-portal.overview' : 'asset.overview',
            params: {
                portfolio_id: asset.portfolio.id,
                asset_id: asset.id,
                reporting_year: resolveAssetReportingYear(asset),
            },
        }
    }

    function portfolioRoutePreserving(portfolio: BreadcrumbPortfolio): RouteLocationRaw {
        const routeName = route.name?.toString() ?? ''

        const isOnPortfolioPage = routeName.startsWith('portfolio.')
          || routeName.startsWith('portfolio-asset.')
          || routeName.startsWith('reports.portfolio.')

        if (isOnPortfolioPage) {
            return {
                name: route.name,
                params: {
                    ...route.params,
                    portfolio_id: portfolio.id,
                    reporting_year: resolvePortfolioReportingYear(portfolio),
                },
            } as RouteLocationRaw
        }

        return portfolioRoute(portfolio)
    }

    function assetRoutePreserving(asset: BreadcrumbAsset): RouteLocationRaw {
        const isOnAssetPage = route.name?.toString().startsWith('asset.')

        if (isOnAssetPage) {
            return {
                name: route.name,
                params: {
                    ...route.params,
                    portfolio_id: asset.portfolio.id,
                    asset_id: asset.id,
                    reporting_year: resolveAssetReportingYear(asset),
                },
            } as RouteLocationRaw
        }

        return assetRoute(asset)
    }

    function mirrorRoute(): RouteLocationRaw | null {
        const redirectsTo = route.meta?.redirectsTo instanceof Function
            ? route.meta.redirectsTo(canAccessFeature)
            : route.meta?.redirectsTo

        if (!redirectsTo || !router.hasRoute(redirectsTo)) {
            return null
        }

        return {
            name: redirectsTo as RouteRecordName,
            params: route.params,
        } as RouteLocationRaw
    }

    /**
     * Default route for Analytics mode when no stored route exists.
     * Single-portfolio companies skip to portfolio overview.
     * Asset-only users go to company.assets, others go to company.overview.
     */
    function defaultAnalyticsRoute(reportingYear?: number): RouteLocationRaw {
        if (hasSinglePortfolio.value) {
            return portfolioRoute(portfolios.value[0], { mode: 'analytics' })
        }

        const year = reportingYear ?? getRouteYear() ?? fallbackYear
        return {
            name: isAssetOnlyAccess.value ? 'company.assets' : 'company.overview',
            params: { reporting_year: year },
        }
    }

    /**
     * Default route for Collection mode when no stored route exists.
     * Single-portfolio companies skip to portfolio input overview.
     * Asset-only users will be redirected from the company overview to the appropriate asset list.
     */
    function defaultCollectionRoute(reportingYear?: number): RouteLocationRaw {
        if (hasSinglePortfolio.value) {
            return portfolioRoute(portfolios.value[0], { mode: 'collection' })
        }

        const year = reportingYear ?? getRouteYear() ?? fallbackYear
        return {
            name: 'company.collection.overview',
            params: { reporting_year: year },
        }
    }

    /**
     * Default route for Reports mode when no stored route exists.
     * Single-portfolio companies skip to portfolio reports overview.
     */
    function defaultReportsRoute(reportingYear?: number): RouteLocationRaw {
        if (hasSinglePortfolio.value) {
            return portfolioRoute(portfolios.value[0], { mode: 'reports' })
        }

        const year = reportingYear ?? getRouteYear() ?? fallbackYear
        return {
            name: 'reports.company.overview',
            params: { reporting_year: year },
        }
    }

    return {
        companyRoute,
        companyRoutePreserving,
        portfolioRoute,
        portfolioListRoute,
        assetRoute,
        portfolioRoutePreserving,
        assetRoutePreserving,
        mirrorRoute,
        defaultAnalyticsRoute,
        defaultCollectionRoute,
        defaultReportsRoute,
    }
}
